import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pause, Play } from 'lucide-react';
import { memberApi, DAY_OPTIONS, daysummary, inputCls } from './memberApi';

export const ProtocolsTab = ({ protocols, peptides, refresh }) => {
  const NON_INJECTABLE = /case|serum|combo|syringe|bac water|bacteriostatic|acetic/i;
  const injectables = peptides.filter(p => p.category !== 'Accessories' && p.category !== 'Skincare' && !NON_INJECTABLE.test(p.name));
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [customName, setCustomName] = useState('');
  const [dose, setDose] = useState('');
  const [days, setDays] = useState([]);
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const toggleDay = (key) => setDays(d => d.includes(key) ? d.filter(x => x !== key) : [...d, key]);

  const save = async () => {
    const peptideName = name === '__custom__' ? customName.trim() : name;
    if (!peptideName || !dose.trim() || days.length === 0) {
      toast.error('Pick a peptide, dose, and at least one day.');
      return;
    }
    setBusy(true);
    try {
      await memberApi.post('/member/protocols', { peptide_name: peptideName, dose: dose.trim(), days, time_of_day: time, notes });
      toast.success('Protocol added!');
      setShowForm(false);
      setName(''); setCustomName(''); setDose(''); setDays([]); setTime(''); setNotes('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save protocol.');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await memberApi.patch(`/member/protocols/${p.protocol_id}`, { active: !p.active });
      refresh();
    } catch {
      toast.error('Failed to update protocol.');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the ${p.peptide_name} protocol?`)) return;
    try {
      await memberApi.delete(`/member/protocols/${p.protocol_id}`);
      toast.success('Protocol deleted.');
      refresh();
    } catch {
      toast.error('Failed to delete protocol.');
    }
  };

  return (
    <div data-testid="protocols-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-400 text-sm">Enter the protocol we emailed you with your order.</p>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#A01830] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors" data-testid="add-protocol-btn">
          <Plus size={14} /> Add Protocol
        </button>
      </div>

      {showForm && (
        <div className="bg-[#0A0A0A] border border-[#D4AF37]/50 rounded-sm p-5 mb-5 space-y-3" data-testid="protocol-form">
          <select value={name} onChange={e => setName(e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="protocol-peptide-select">
            <option value="">Select a peptide...</option>
            {injectables.map(p => <option key={p.id} value={p.name}>{p.name} — {p.dosage}</option>)}
            <option value="__custom__">Other (type it in)</option>
          </select>
          {name === '__custom__' && (
            <input value={customName} onChange={e => setCustomName(e.target.value)} placeholder="Peptide name" className={inputCls} data-testid="protocol-custom-name" />
          )}
          <input value={dose} onChange={e => setDose(e.target.value)} placeholder="Dose (e.g. 500mcg, 2.5mg)" className={inputCls} data-testid="protocol-dose-input" />
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Days</p>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setDays(days.length === 7 ? [] : DAY_OPTIONS.map(d => d.key))} className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border transition-colors ${days.length === 7 ? 'bg-[#C41E3A] border-[#C41E3A] text-white' : 'border-[#27272A] text-gray-400 hover:border-[#C41E3A]'}`} data-testid="protocol-day-daily">
                Daily
              </button>
              {DAY_OPTIONS.map(d => (
                <button key={d.key} type="button" onClick={() => toggleDay(d.key)} className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border transition-colors ${days.includes(d.key) ? 'bg-[#C41E3A] border-[#C41E3A] text-white' : 'border-[#27272A] text-gray-400 hover:border-[#C41E3A]'}`} data-testid={`protocol-day-${d.key}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="time" value={time} onChange={e => setTime(e.target.value)} className={inputCls} data-testid="protocol-time-input" />
            <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" className={inputCls} data-testid="protocol-notes-input" />
          </div>
          <button onClick={save} disabled={busy} className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-colors" data-testid="protocol-save-btn">
            {busy ? 'Saving...' : 'Save Protocol'}
          </button>
        </div>
      )}

      {protocols.length === 0 && !showForm ? (
        <p className="text-gray-500 text-sm text-center py-8" data-testid="no-protocols">No protocols yet — add the one from your order email to unlock your dashboard.</p>
      ) : (
        <div className="space-y-3">
          {protocols.map(p => (
            <div key={p.protocol_id} className={`bg-[#0A0A0A] border rounded-sm p-4 flex items-center justify-between gap-3 ${p.active ? 'border-[#27272A]' : 'border-[#27272A] opacity-50'}`} data-testid={`protocol-${p.protocol_id}`}>
              <div className="min-w-0">
                <p className="text-white text-sm font-semibold">{p.peptide_name} <span className="text-[#D4AF37] font-normal">— {p.dose}</span></p>
                <p className="text-gray-500 text-xs mt-0.5">{daysummary(p.days)}{p.time_of_day ? ` · ${p.time_of_day}` : ''}{p.notes ? ` · ${p.notes}` : ''}</p>
                {!p.active && <p className="text-gray-600 text-[10px] uppercase tracking-wider mt-1">Paused</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleActive(p)} className="text-gray-400 hover:text-[#D4AF37] transition-colors" title={p.active ? 'Pause' : 'Resume'} data-testid={`protocol-toggle-${p.protocol_id}`}>
                  {p.active ? <Pause size={16} /> : <Play size={16} />}
                </button>
                <button onClick={() => remove(p)} className="text-gray-400 hover:text-[#C41E3A] transition-colors" data-testid={`protocol-delete-${p.protocol_id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
